import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Delete, LogIn, Loader2, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ProductSymbol } from "@/components/common";
import { LOGO_URL, PRODUCT_SUBNAME, DESCRIPTOR } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";

function routeFor(user, nav) {
  if (user.role === "ADMIN") nav(user.onboarding_completed ? "/admin" : "/onboarding");
  else nav("/app");
}

export default function Login() {
  const { login, pinLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  useEffect(() => { api.get("/").then(({ data }) => setShowDemo(!!data.demo)).catch(() => {}); }, []);

  const doEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { routeFor(await login(email, password), nav); }
    catch (err) { toast.error(apiErr(err)); }
    finally { setLoading(false); }
  };

  const doPin = async () => {
    if (!email) { toast.error("Ingresa tu correo"); return; }
    if (pin.length !== 6) { toast.error("El PIN debe tener 6 dígitos"); return; }
    setLoading(true);
    try { routeFor(await pinLogin(email, pin), nav); }
    catch (err) { toast.error(apiErr(err)); setPin(""); }
    finally { setLoading(false); }
  };

  const pressDigit = (d) => setPin((prev) => (prev + d).slice(0, 6));

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <ProductSymbol />
          <div className="mt-5 flex items-center justify-center gap-2.5">
            <img src={LOGO_URL} alt="TUPU" className="h-8 w-auto object-contain" />
            <span className="wordmark text-3xl text-[#2563EB]">{PRODUCT_SUBNAME}</span>
          </div>
          <p className="mt-2 text-sm text-[#68756F]">{DESCRIPTOR}</p>
        </div>

        <div className="mt-8 rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
          <h1 className="font-display text-xl font-semibold text-[#153D35]">Iniciar sesión</h1>
          <p className="mt-1 text-sm text-[#68756F]">Accede con tu cuenta o PIN operacional.</p>

          <Tabs defaultValue="pin" className="mt-5">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="pin" data-testid="tab-pin"><KeyRound className="mr-2 h-4 w-4" /> PIN</TabsTrigger>
              <TabsTrigger value="email" data-testid="tab-email"><Mail className="mr-2 h-4 w-4" /> Correo</TabsTrigger>
            </TabsList>

            <TabsContent value="pin" className="mt-5">
              <div className="mb-4">
                <Label className="text-sm text-[#153D35]">Correo</Label>
                <Input data-testid="pin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-12" placeholder="tu@empresa.cl" />
              </div>
              <div className="mb-5 flex justify-center gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className={`h-3 w-3 rounded-full ${i < pin.length ? "bg-[#2563EB]" : "bg-slate-200"}`} />
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                  <button key={d} data-testid={`pin-key-${d}`} onClick={() => pressDigit(String(d))}
                    className="h-16 rounded-xl bg-slate-100 text-2xl font-medium text-[#153D35] transition-colors hover:bg-slate-200 active:scale-95">
                    {d}
                  </button>
                ))}
                <button data-testid="pin-clear" onClick={() => setPin("")} className="h-16 rounded-xl text-sm font-medium text-[#68756F] hover:bg-slate-100">Borrar</button>
                <button data-testid="pin-key-0" onClick={() => pressDigit("0")} className="h-16 rounded-xl bg-slate-100 text-2xl font-medium text-[#153D35] hover:bg-slate-200 active:scale-95">0</button>
                <button data-testid="pin-backspace" onClick={() => setPin(pin.slice(0, -1))} className="grid h-16 place-items-center rounded-xl text-[#68756F] hover:bg-slate-100"><Delete className="h-5 w-5" /></button>
              </div>
              <Button data-testid="pin-submit-btn" disabled={loading || pin.length !== 6 || !email} onClick={doPin} className="mt-5 h-14 w-full rounded-xl bg-[#2563EB] text-base hover:bg-[#1D4ED8]">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Ingresar con PIN</>}
              </Button>
            </TabsContent>

            <TabsContent value="email" className="mt-5">
              <form onSubmit={doEmail} className="space-y-4">
                <div>
                  <Label className="text-sm text-[#153D35]">Correo</Label>
                  <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-12" placeholder="tu@empresa.cl" required />
                </div>
                <div>
                  <Label className="text-sm text-[#153D35]">Contraseña</Label>
                  <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-12" placeholder="••••••••" required />
                </div>
                <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="h-12 w-full rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogIn className="mr-2 h-4 w-4" /> Entrar</>}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {showDemo && (
          <div className="mt-6 rounded-xl border border-[#E2E8F0] bg-white p-4 text-xs text-[#68756F]">
            <p className="mb-1 font-medium text-[#153D35]">Credenciales demo</p>
            <p>Admin: <span className="font-mono">admin@demo.cl</span> / <span className="font-mono">admin123</span></p>
            <p>Operador: <span className="font-mono">user@demo.cl</span> + PIN <span className="font-mono">123456</span></p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
